import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback, useMemo } from 'react';
import styles from './ImageUploadStep.module.css';
import coreStyles from './DetailsForm.module.css';
import { FaPlus, FaGripVertical } from 'react-icons/fa';
import AuthContext from '../../../context/AuthContext';
import { useFormStep } from '../../../context/FormStepContext';
import {
  EditImageModal,
  RemoveBackgroundModal,
  DeleteImagesModal,
  DeleteConfirmationModal,
  RestoreImagesModal,
  AddZoomModal,
  AddZoomAreasModal
} from './modals';
import { THEME_CONFIG, APLUS_NO_IMAGE_CONFIG, THEME_IMAGE_COUNT, aplusPageNeedsImage } from '../constants';
import themeThumbnailUrls from '../../../assets/theme_thumbnails_urls.json';
import themeSlideCounts from '../../../assets/theme_slide_counts.json';
import zoomshotBgUrls from '../../../assets/zoomshot_bg_urls.json';

const dimensionsOverlay = themeThumbnailUrls.dimensions;

const slideCount = (themeId, type) => {
  const counts = themeSlideCounts[themeId];
  return counts && counts[type] != null ? counts[type] : 0;
};

const isNewFormat = (entry) => !!(entry && (entry.feature || entry.main || entry.aplus));

// New-format B+ slot order: features → application → zoomshot → lifestyle.
// Returns null for legacy themes so callers can fall back.
const buildBPlusSlots = (themeId) => {
  const entry = themeThumbnailUrls[themeId];
  if (!isNewFormat(entry)) return null;
  const slots = [];
  (entry.feature || []).forEach((thumb) => slots.push({ kind: 'feature', thumb }));
  if (entry.application) slots.push({ kind: 'application', thumb: entry.application });
  if (entry.zoomshot) slots.push({ kind: 'zoomshot', thumb: entry.zoomshot });
  if (entry.lifestyle) slots.push({ kind: 'lifestyle', thumb: entry.lifestyle });
  return slots;
};

const loadThemeImage = (themeId, type, number) => {
  const urls = themeThumbnailUrls[themeId]?.[`${type}_thumbnails`];
  if (!urls) return null;
  // Newer zoomshot/lifestyle thumbnails are named `${themeId}_template${number}.png`
  // and don't follow the `${type}_${themeId}.${number}.` convention. Prefer the
  // alternate when it exists (only present for the relevant slots).
  const alternate = `${themeId}_template${number}.`;
  const primary = `${type}_${themeId}.${number}.`;
  return urls.find(u => u.includes(alternate))
      || urls.find(u => u.includes(primary))
      || null;
};

// Constants
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_MAX_LIMIT = 10;

const ImageUploadStep = ({
  bPlusSequence = [],
  aPlusSequence = [],
  onBPlusSequenceChange,
  onAPlusSequenceChange,
  zoomAreas = [],
  onZoomAreasChange,
  isLoading = false,
  listingMode = 'new',
  dimensionData = null,
  setDimensionData = null,
  productDimensionsRef,
  openRemoveBgSignal = 0
}) => {
  const authCtx = useContext(AuthContext);
  const { selectedTheme } = useFormStep();
  
  const [localBPlus, setLocalBPlus] = useState(bPlusSequence || []);
  const [localAPlus, setLocalAPlus] = useState(aPlusSequence || []);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedSequence, setDraggedSequence] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState({ index: null, sequence: null });
  const [editModal, setEditModal] = useState({ show: false, index: null, sequenceType: null });
  const [processingImage, setProcessingImage] = useState(false);
  const [removeBgModal, setRemoveBgModal] = useState(false);
  const [selectedImagesForBgRemoval, setSelectedImagesForBgRemoval] = useState(new Set());
  const [processingBgRemoval, setProcessingBgRemoval] = useState(false);
  const [deleteImagesModal, setDeleteImagesModal] = useState(false);
  const [selectedImagesForDeletion, setSelectedImagesForDeletion] = useState(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletedImages, setDeletedImages] = useState([]); // Store deleted images with metadata
  const [restoreImagesModal, setRestoreImagesModal] = useState(false);
  const [selectedImagesForRestore, setSelectedImagesForRestore] = useState(new Set());
  const [copyFromBPlus, setCopyFromBPlus] = useState(false);
  const [addZoomModal, setAddZoomModal] = useState({ show: false, image: null });
  const [addZoomAreasModal, setAddZoomAreasModal] = useState({ show: false, image: null });
  
  const bPlusFileInputRef = useRef(null);
  const aPlusFileInputRef = useRef(null);
  const dimensionImageInputRef = useRef(null);

  // Sync with props when they change
  useEffect(() => {
    if (bPlusSequence !== undefined) {
      setLocalBPlus(bPlusSequence);
    }
  }, [bPlusSequence]);

  useEffect(() => {
    if (aPlusSequence !== undefined) {
      setLocalAPlus(aPlusSequence);
    }
  }, [aPlusSequence]);

  // Copy B+ images to A+ when checkbox is checked
  useEffect(() => {
    if (copyFromBPlus && localBPlus && localBPlus.length > 0) {
      // Create a deep copy of B+ images
      const copiedImages = localBPlus.map(img => ({
        ...img,
        file: img.file,
        previewUrl: img.previewUrl,
        imagePath: img.imagePath
      }));
      setLocalAPlus(copiedImages);
      if (onAPlusSequenceChange) {
        onAPlusSequenceChange(copiedImages);
      }
    }
  }, [copyFromBPlus, localBPlus, onAPlusSequenceChange]);

  // Open the Remove Background modal when the parent signals it (e.g. from the
  // pre-generation reminder). useLayoutEffect (not useEffect) so the modal opens
  // in the same paint the reminder closes — avoids a one-frame backdrop flicker.
  // Skipped on initial mount (signal starts at 0).
  useLayoutEffect(() => {
    if (openRemoveBgSignal > 0) {
      setRemoveBgModal(true);
      setSelectedImagesForBgRemoval(new Set());
    }
  }, [openRemoveBgSignal]);

  // Track if user manually removed dimension image
  const userRemovedDimensionImage = useRef(false);

  // Track in-flight auto background removal for dimension image
  const isAutoRemovingDimensionBg = useRef(false);

  // Auto-populate dimension image from first B+ image if not set (and user hasn't manually removed it)
  useEffect(() => {
    if (localBPlus && localBPlus.length > 0 && localBPlus[0] && !dimensionData?.dimensionImage && setDimensionData && !userRemovedDimensionImage.current) {
      setDimensionData(prev => ({
        ...(prev || {}),
        length: prev?.length || '',
        breadth: prev?.breadth || '',
        height: prev?.height || '',
        units: prev?.units || 'cm',
        isComplete: prev?.isComplete || false,
        dimensionImage: localBPlus[0]
      }));
    }
  }, [localBPlus, dimensionData?.dimensionImage, setDimensionData]);

  // Helper function to get sequence and setter based on type
  const getSequenceData = (sequenceType) => {
    if (sequenceType === 'bPlus') {
      return {
        sequence: localBPlus,
        setSequence: setLocalBPlus,
        onChange: onBPlusSequenceChange
      };
    }
    return {
      sequence: localAPlus,
      setSequence: setLocalAPlus,
      onChange: onAPlusSequenceChange
    };
  };

  // Dynamic max limit based on theme
  const getDynamicMaxLimit = useCallback((sequenceType) => {
    if (!selectedTheme || !selectedTheme.id) return DEFAULT_MAX_LIMIT;
    const themeId = selectedTheme.id;
    const type = sequenceType === 'bPlus' ? 'bplus' : 'aplus';
    return THEME_IMAGE_COUNT[themeId]?.[type] ?? DEFAULT_MAX_LIMIT;
  }, [selectedTheme]);

  // Helper function to validate and process file
  const processFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return null;
    }
    if (file.size > MAX_FILE_SIZE) {
      return null;
    }
    return { file, previewUrl: URL.createObjectURL(file) };
  };

  // Handle dimension image upload (just set; background removal is handled by effect)
  const handleDimensionImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const processedImage = processFile(file);
    if (processedImage && setDimensionData) {
      userRemovedDimensionImage.current = false; // Reset flag when user uploads new image
      setDimensionData(prev => ({
        ...(prev || {}),
        length: prev?.length || '',
        breadth: prev?.breadth || '',
        height: prev?.height || '',
        units: prev?.units || 'cm',
        isComplete: prev?.isComplete || false,
        dimensionImage: processedImage
      }));
    }
    e.target.value = '';
  };

  // Automatically remove background for dimension image whenever one is present
  useEffect(() => {
    const autoRemoveBgForDimension = async () => {
      if (!dimensionData?.dimensionImage) return;

      const image = dimensionData.dimensionImage;

      // Only process images that have a File and haven't already had background removed
      if (!image.file || image.backgroundRemoved || isAutoRemovingDimensionBg.current) {
        return;
      }

      isAutoRemovingDimensionBg.current = true;

      try {
        const formData = new FormData();
        formData.append('image', image.file);

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/single-gen/remove-background`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authCtx?.token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Background removal failed');
        }

        const result = await response.json();

        // Fetch the processed image file
        const processedImageResponse = await fetch(result.image_url);
        const blob = await processedImageResponse.blob();
        const processedFile = new File([blob], image.file.name, { type: blob.type });
        if (image.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(image.previewUrl);
        const processedPreviewUrl = URL.createObjectURL(processedFile);

        const bgRemovedImage = {
          ...image,
          file: processedFile,
          previewUrl: processedPreviewUrl,
          backgroundRemoved: true,
        };

        if (setDimensionData) {
          setDimensionData(prev => ({
            ...(prev || {}),
            dimensionImage: bgRemovedImage,
          }));
        }
      } catch (err) {
        console.error('Auto background removal for dimension image failed:', err);
      } finally {
        isAutoRemovingDimensionBg.current = false;
      }
    };

    autoRemoveBgForDimension();
  }, [dimensionData?.dimensionImage, authCtx, setDimensionData]);

  // Remove dimension image
  const removeDimensionImage = () => {
    userRemovedDimensionImage.current = true; // Prevent auto-populate from re-setting it

    // When removing the dimension image, store it in deletedImages so it can be restored
    if (dimensionData?.dimensionImage) {
      const imageToDelete = dimensionData.dimensionImage;
      const timestamp = Date.now();
      const deletedEntry = {
        image: imageToDelete,
        sequenceType: 'dimension',
        originalIndex: 0,
        deletedAt: timestamp,
        uniqueId: `deleted-dimension-0-${timestamp}-${Math.random().toString(36).substr(2, 9)}`
      };
      setDeletedImages(prev => [...prev, deletedEntry]);
    }

    if (setDimensionData) {
      setDimensionData(prev => ({
        ...(prev || {}),
        dimensionImage: null
      }));
    }
  };

  const handleFileUpload = (e, sequenceType) => {
    const files = Array.from(e.target.files);
    const { sequence, setSequence, onChange } = getSequenceData(sequenceType);
    
    const newImages = files
      .map(processFile)
      .filter(Boolean); // Remove null values

    if (newImages.length > 0) {
      const currentMaxLimit = getDynamicMaxLimit(sequenceType);
      const remainingSlots = currentMaxLimit - sequence.length;

      if (remainingSlots <= 0) {
        alert(`Maximum number of product images for this theme reached.`);
        e.target.value = '';
        return;
      }

      const imagesToAdd = newImages.slice(0, remainingSlots);
      const newSequence = [...sequence, ...imagesToAdd];
      
      setSequence(newSequence);
      if (onChange) onChange(newSequence);
    }

    // Reset file input
    e.target.value = '';
  };

  const handleDragStart = (e, index, sequenceType) => {
    setDraggedItem(index);
    setDraggedSequence(sequenceType);
    e.dataTransfer.effectAllowed = 'move';

    // Add a slight delay to allow the ghost image to be created before hiding the original
    setTimeout(() => {
      const target = e.target;
      if (target && target.classList) {
        target.classList.add(styles.dragging);
      }
    }, 0);
  };

  const handleDragOver = (e, index, sequenceType) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Allow drag over any sequence (same or different)
      setDragOverIndex({ index, sequence: sequenceType });
  };

  const handleDragLeave = () => {
    setDragOverIndex({ index: null, sequence: null });
  };

  const handleDrop = (e, dropIndex, sequenceType) => {
    e.preventDefault();
    setDragOverIndex({ index: null, sequence: null });

    if (draggedItem === null || draggedSequence === null) {
      setDraggedItem(null);
      setDraggedSequence(null);
      return;
    }

    const sourceData = getSequenceData(draggedSequence);
    const targetData = getSequenceData(sequenceType);

    // Check if target sequence is full
    const currentMaxLimit = getDynamicMaxLimit(sequenceType);
    if (draggedSequence !== sequenceType && targetData.sequence.length >= currentMaxLimit) {
      alert(`Maximum number of product images for this theme reached.`);
      setDraggedItem(null);
      setDraggedSequence(null);
      return;
    }

    const sourceSequence = [...sourceData.sequence];
    const draggedImage = sourceSequence[draggedItem];

    if (draggedSequence === sequenceType) {
      // Same sequence - reorder
      sourceSequence.splice(draggedItem, 1);
      sourceSequence.splice(dropIndex, 0, draggedImage);
      sourceData.setSequence(sourceSequence);
      if (sourceData.onChange) sourceData.onChange(sourceSequence);
    } else {
      // Cross-sequence - duplicate (keep in source, add to target)
      const targetSequence = [...targetData.sequence];
      targetSequence.splice(dropIndex, 0, draggedImage);
      targetData.setSequence(targetSequence);
      if (targetData.onChange) targetData.onChange(targetSequence);
    }

    setDraggedItem(null);
    setDraggedSequence(null);
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
    setDraggedSequence(null);
    setDragOverIndex({ index: null, sequence: null });

    // Remove dragging class from all elements
    const draggingElements = document.querySelectorAll(`.${styles.dragging}`);
    draggingElements.forEach(el => el.classList.remove(styles.dragging));
  };

  const removeImageFromSequence = (index, sequenceType) => {
    const { sequence, setSequence, onChange } = getSequenceData(sequenceType);
    const newSequence = sequence.filter((_, i) => i !== index);
    setSequence(newSequence);
    if (onChange) onChange(newSequence);
  };

  const handleEditImage = (index, sequenceType) => {
    setEditModal({ show: true, index, sequenceType });
  };

  const closeEditModal = () => {
    setEditModal({ show: false, index: null, sequenceType: null });
  };

  const handleRemoveBackground = async () => {
    if (!editModal.show || editModal.index === null || !editModal.sequenceType) return;
    
    const sequence = editModal.sequenceType === 'bPlus' ? localBPlus : localAPlus;
    const image = sequence[editModal.index];
    
    if (!image || !image.file) {
      closeEditModal();
      return;
    }

    setProcessingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', image.file);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/single-gen/remove-background`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx?.token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Background removal failed');
      }

      const result = await response.json();
      
      // Fetch the processed image
      const processedImageResponse = await fetch(result.image_url);
      const blob = await processedImageResponse.blob();
      const processedFile = new File([blob], image.file.name, { type: blob.type });
      const processedPreviewUrl = URL.createObjectURL(processedFile);

      // Update the sequence with the processed image
      const updatedImage = {
        ...image,
        file: processedFile,
        previewUrl: processedPreviewUrl,
        backgroundRemoved: true
      };

      if (editModal.sequenceType === 'bPlus') {
        const newSequence = [...localBPlus];
        newSequence[editModal.index] = updatedImage;
        setLocalBPlus(newSequence);
        if (onBPlusSequenceChange) onBPlusSequenceChange(newSequence);
      } else if (editModal.sequenceType === 'aPlus') {
        const newSequence = [...localAPlus];
        newSequence[editModal.index] = updatedImage;
        setLocalAPlus(newSequence);
        if (onAPlusSequenceChange) onAPlusSequenceChange(newSequence);
      }

      closeEditModal();
    } catch (error) {
      alert('Failed to remove background. Please try again.');
    } finally {
      setProcessingImage(false);
    }
  };

  // Get unique images from both sequences (for remove background only). Exclude dimension image
  // since it has its own separate auto remove-background flow and should not appear in this modal.
  const uniqueImages = useMemo(() => {
    const allImages = [];
    const seenUrls = new Set();

    // Add B+ images with metadata
    if (localBPlus && Array.isArray(localBPlus)) {
      localBPlus.forEach((image, index) => {
        if (!image) return;
        const url = image.previewUrl || image.file?.name;
        if (url && !seenUrls.has(url)) {
          seenUrls.add(url);
          allImages.push({
            image: image,
            sequenceType: 'bPlus',
            originalIndex: index,
            uniqueId: `bPlus-${index}-${url}`
          });
        }
      });
    }

    // Add A+ images with metadata (only if not already seen)
    if (localAPlus && Array.isArray(localAPlus)) {
      localAPlus.forEach((image, index) => {
        if (!image) return;
        const url = image.previewUrl || image.file?.name;
        if (url && !seenUrls.has(url)) {
          seenUrls.add(url);
          allImages.push({
            image: image,
            sequenceType: 'aPlus',
            originalIndex: index,
            uniqueId: `aPlus-${index}-${url}`
          });
        }
      });
    }

    return allImages;
  }, [localBPlus, localAPlus]);

  // Get all images from both sequences (for delete images - includes duplicates)
  const allImages = useMemo(() => {
    const images = [];
    
    // Add B+ images with metadata
    if (localBPlus && Array.isArray(localBPlus)) {
      localBPlus.forEach((image, index) => {
        if (!image) return;
        images.push({
          image: image,
          sequenceType: 'bPlus',
          originalIndex: index,
          uniqueId: `bPlus-${index}-${image.previewUrl || image.file?.name || index}`
        });
      });
    }
    
    // Add A+ images with metadata
    if (localAPlus && Array.isArray(localAPlus)) {
      localAPlus.forEach((image, index) => {
        if (!image) return;
        images.push({
          image: image,
          sequenceType: 'aPlus',
          originalIndex: index,
          uniqueId: `aPlus-${index}-${image.previewUrl || image.file?.name || index}`
        });
      });
    }
    
    return images;
  }, [localBPlus, localAPlus]);

  // Generic toggle selection helper
  const createToggleSelection = (selectedSet, setSelectedSet) => (uniqueId) => {
    const newSelected = new Set(selectedSet);
    if (newSelected.has(uniqueId)) {
      newSelected.delete(uniqueId);
    } else {
      newSelected.add(uniqueId);
    }
    setSelectedSet(newSelected);
  };

  // Generic select all helper
  const createSelectAll = (selectedSet, setSelectedSet, allItems) => () => {
    if (selectedSet.size === allItems.length) {
      setSelectedSet(new Set());
    } else {
      setSelectedSet(new Set(allItems.map(item => item.uniqueId)));
    }
  };

  const handleToggleImageSelection = useCallback(
    createToggleSelection(selectedImagesForBgRemoval, setSelectedImagesForBgRemoval),
    [selectedImagesForBgRemoval]
  );
  
  const handleSelectAll = useCallback(() => {
    const selectableForBgRemoval = uniqueImages.filter(
      img => img?.image && !img.image.backgroundRemoved
    );
    createSelectAll(selectedImagesForBgRemoval, setSelectedImagesForBgRemoval, selectableForBgRemoval)();
  }, [selectedImagesForBgRemoval, uniqueImages]);

  const handleRemoveBackgroundMultiple = async () => {
    if (selectedImagesForBgRemoval.size === 0) return;

    setProcessingBgRemoval(true);
    try {
      const selectedUniqueIds = Array.from(selectedImagesForBgRemoval);
      const selectedImages = uniqueImages.filter(img => img && selectedUniqueIds.includes(img.uniqueId));

      // Group images by URL to avoid duplicate processing
      const imagesByUrl = new Map();
      selectedImages.forEach(imageData => {
        if (!imageData || !imageData.image || !imageData.image.file) return;
        const imageUrl = imageData.image.previewUrl || imageData.image.file?.name;
        if (!imageUrl) return;
        
        if (!imagesByUrl.has(imageUrl)) {
          imagesByUrl.set(imageUrl, {
            image: imageData.image,
            imageDataList: []
          });
        }
        imagesByUrl.get(imageUrl).imageDataList.push(imageData);
      });

      // Process images in batches of 4
      const BATCH_SIZE = 4;
      const allEntries = Array.from(imagesByUrl.entries());
      const results = [];

      for (let i = 0; i < allEntries.length; i += BATCH_SIZE) {
        const batch = allEntries.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async ([imageUrl, { image, imageDataList }]) => {
          try {
            const formData = new FormData();
            formData.append('image', image.file);

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/single-gen/remove-background`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authCtx?.token}`
              },
              body: formData
            });

            if (!response.ok) {
              throw new Error('Background removal failed');
            }

            const result = await response.json();

            // Fetch the processed image
            const processedImageResponse = await fetch(result.image_url);
            const blob = await processedImageResponse.blob();
            const processedFile = new File([blob], image.file.name, { type: blob.type });
            const processedPreviewUrl = URL.createObjectURL(processedFile);

            return {
              imageUrl,
              processedImage: {
                ...image,
                file: processedFile,
                previewUrl: processedPreviewUrl,
                backgroundRemoved: true
              },
              imageDataList
            };
          } catch (error) {
            return {
              imageUrl,
              processedImage: null,
              imageDataList,
              error
            };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }

      // Update sequences with processed images
      const updatedBPlus = [...(localBPlus || [])];
      const updatedAPlus = [...(localAPlus || [])];

      let updatedDimensionImage = dimensionData?.dimensionImage || null;

      results.forEach(({ imageUrl, processedImage, imageDataList, error }) => {
        if (error || !processedImage) return; // Skip failed images

        // Update all instances of this image
        imageDataList.forEach(({ sequenceType, originalIndex }) => {
          if (sequenceType === 'dimension') {
            updatedDimensionImage = processedImage;
          } else if (sequenceType === 'bPlus' && originalIndex < updatedBPlus.length) {
            updatedBPlus[originalIndex] = processedImage;
          } else if (sequenceType === 'aPlus' && originalIndex < updatedAPlus.length) {
            updatedAPlus[originalIndex] = processedImage;
          }
        });

        // Update dimension image if URL matches
        if (updatedDimensionImage) {
          const dimUrl = updatedDimensionImage.previewUrl || updatedDimensionImage.file?.name;
          if (dimUrl === imageUrl && updatedDimensionImage !== processedImage) {
            updatedDimensionImage = { ...processedImage };
          }
        }

        // Update all other instances with matching URL in both sequences
        updatedBPlus.forEach((img, idx) => {
          const imgUrl = img?.previewUrl || img?.file?.name;
          if (imgUrl === imageUrl && img !== processedImage) {
            updatedBPlus[idx] = { ...processedImage };
          }
        });

        updatedAPlus.forEach((img, idx) => {
          const imgUrl = img?.previewUrl || img?.file?.name;
          if (imgUrl === imageUrl && img !== processedImage) {
            updatedAPlus[idx] = { ...processedImage };
          }
        });
      });

      setLocalBPlus(updatedBPlus);
      setLocalAPlus(updatedAPlus);
      if (onBPlusSequenceChange) onBPlusSequenceChange(updatedBPlus);
      if (onAPlusSequenceChange) onAPlusSequenceChange(updatedAPlus);

      // Update dimension image if it was processed
      if (updatedDimensionImage !== dimensionData?.dimensionImage && setDimensionData) {
        setDimensionData(prev => ({
          ...(prev || {}),
          dimensionImage: updatedDimensionImage
        }));
      }
      
      setRemoveBgModal(false);
      setSelectedImagesForBgRemoval(new Set());
    } catch (error) {
      alert('Failed to remove backgrounds. Please try again.');
    } finally {
      setProcessingBgRemoval(false);
    }
  };

  const handleToggleImageSelectionForDeletion = useCallback(
    createToggleSelection(selectedImagesForDeletion, setSelectedImagesForDeletion),
    [selectedImagesForDeletion]
  );
  
  const handleSelectAllForDeletion = useCallback(() => {
    createSelectAll(selectedImagesForDeletion, setSelectedImagesForDeletion, allImages)();
  }, [selectedImagesForDeletion, allImages]);

  const handleDeleteSelectedImages = () => {
    if (selectedImagesForDeletion.size === 0) return;
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteImages = () => {
    if (selectedImagesForDeletion.size === 0) return;

    const selectedUniqueIds = Array.from(selectedImagesForDeletion);
    const selectedImages = allImages.filter(img => img && selectedUniqueIds.includes(img.uniqueId));

    const updatedBPlus = [...(localBPlus || [])];
    const updatedAPlus = [...(localAPlus || [])];

    // Store deleted images with metadata for restoration
    const timestamp = Date.now();
    const imagesToStore = selectedImages.map(({ image, sequenceType, originalIndex }, idx) => ({
      image,
      sequenceType,
      originalIndex,
      deletedAt: timestamp,
      uniqueId: `deleted-${sequenceType}-${originalIndex}-${timestamp}-${idx}-${Math.random().toString(36).substr(2, 9)}`
    }));

    // Add to deleted images array
    setDeletedImages(prev => [...prev, ...imagesToStore]);

    // Group deletions by sequence type and sort indices in descending order
    const bPlusIndicesToDelete = [];
    const aPlusIndicesToDelete = [];

    selectedImages.forEach(({ sequenceType, originalIndex }) => {
      if (sequenceType === 'bPlus') {
        bPlusIndicesToDelete.push(originalIndex);
      } else if (sequenceType === 'aPlus') {
        aPlusIndicesToDelete.push(originalIndex);
      }
    });

    // Delete from B+ sequence (descending order to avoid index shifting)
    bPlusIndicesToDelete.sort((a, b) => b - a).forEach(index => {
      if (index >= 0 && index < updatedBPlus.length) {
        updatedBPlus.splice(index, 1);
      }
    });

    // Delete from A+ sequence (descending order to avoid index shifting)
    aPlusIndicesToDelete.sort((a, b) => b - a).forEach(index => {
      if (index >= 0 && index < updatedAPlus.length) {
        updatedAPlus.splice(index, 1);
      }
    });

    setLocalBPlus(updatedBPlus);
    setLocalAPlus(updatedAPlus);
    if (onBPlusSequenceChange) onBPlusSequenceChange(updatedBPlus);
    if (onAPlusSequenceChange) onAPlusSequenceChange(updatedAPlus);

    setDeleteImagesModal(false);
    setSelectedImagesForDeletion(new Set());
    setShowDeleteConfirmation(false);
  };

  const cancelDeleteImages = () => {
    setShowDeleteConfirmation(false);
  };

  const handleToggleImageSelectionForRestore = useCallback(
    createToggleSelection(selectedImagesForRestore, setSelectedImagesForRestore),
    [selectedImagesForRestore]
  );
  
  const handleSelectAllForRestore = useCallback(() => {
    createSelectAll(selectedImagesForRestore, setSelectedImagesForRestore, deletedImages)();
  }, [selectedImagesForRestore, deletedImages]);

  const handleRestoreSelectedImages = () => {
    if (selectedImagesForRestore.size === 0) return;

    const selectedUniqueIds = Array.from(selectedImagesForRestore);
    const imagesToRestore = deletedImages.filter(img => img && img.uniqueId && selectedUniqueIds.includes(img.uniqueId));

    if (imagesToRestore.length === 0) {
      return;
    }

    const updatedBPlus = [...(localBPlus || [])];
    const updatedAPlus = [...(localAPlus || [])];

    // Group restorations by sequence type
    const bPlusRestorations = [];
    const aPlusRestorations = [];
    const dimensionRestorations = [];

    imagesToRestore.forEach(({ image, sequenceType, originalIndex, uniqueId }) => {
      if (!image) {
        return;
      }
      if (sequenceType === 'bPlus') {
        bPlusRestorations.push({ image, index: originalIndex, uniqueId });
      } else if (sequenceType === 'aPlus') {
        aPlusRestorations.push({ image, index: originalIndex, uniqueId });
      } else if (sequenceType === 'dimension') {
        dimensionRestorations.push({ image, index: originalIndex, uniqueId });
      }
    });

    // Track which images were successfully restored
    const successfullyRestoredIds = new Set();

    // Restore B+ images (sort by index descending to insert from end to beginning to avoid index shifting)
    const sortedBPlus = [...bPlusRestorations].sort((a, b) => b.index - a.index);
    
    sortedBPlus.forEach(({ image, index, uniqueId }) => {
      if (index >= 0) {
        // Clamp index to valid range: if index > length, append to end
        const insertIndex = Math.min(index, updatedBPlus.length);
        updatedBPlus.splice(insertIndex, 0, image);
        successfullyRestoredIds.add(uniqueId);
      }
    });

    // Restore A+ images (sort by index descending to insert from end to beginning to avoid index shifting)
    const sortedAPlus = [...aPlusRestorations].sort((a, b) => b.index - a.index);
    
    sortedAPlus.forEach(({ image, index, uniqueId }) => {
      if (index >= 0) {
        // Clamp index to valid range: if index > length, append to end
        const insertIndex = Math.min(index, updatedAPlus.length);
        updatedAPlus.splice(insertIndex, 0, image);
        successfullyRestoredIds.add(uniqueId);
      }
    });

    // Restore dimension image (only one slot; use last selected)
    if (dimensionRestorations.length > 0 && setDimensionData) {
      const { image } = dimensionRestorations[dimensionRestorations.length - 1];
      setDimensionData(prev => ({
        ...(prev || {}),
        dimensionImage: image
      }));
      dimensionRestorations.forEach(({ uniqueId }) => {
        successfullyRestoredIds.add(uniqueId);
      });
      // Since user explicitly restored, clear the "user removed" flag
      userRemovedDimensionImage.current = false;
    }

    // Update sequences first
    setLocalBPlus(updatedBPlus);
    setLocalAPlus(updatedAPlus);
    if (onBPlusSequenceChange) onBPlusSequenceChange(updatedBPlus);
    if (onAPlusSequenceChange) onAPlusSequenceChange(updatedAPlus);

    // Remove only successfully restored images from deleted images array
    setDeletedImages(prev => {
      const beforeCount = prev.length;
      const remaining = prev.filter(img => !successfullyRestoredIds.has(img.uniqueId));
      return remaining;
    });

    // Clear selection and close modal
    setSelectedImagesForRestore(new Set());
    setRestoreImagesModal(false);
  };

  // B+ slot count. New-format themes derive it from the JSON entry; legacy
  // themes use bplus templates + 1 for the application slot when present.
  const bplusSlotCount = (themeId) => {
    const slots = buildBPlusSlots(themeId);
    if (slots) return slots.length;
    const config = THEME_CONFIG[themeId];
    const base = (config?.bplus?.length) ?? slideCount(themeId, 'bplus');
    const hasApp = !!themeSlideCounts[themeId]?.application;
    return base + (hasApp ? 1 : 0);
  };

  const themeHasApplication = (themeId) => {
    const slots = buildBPlusSlots(themeId);
    if (slots) return slots.some(s => s.kind === 'application');
    return !!themeSlideCounts[themeId]?.application;
  };

  // Index of the application "No Image" slot, or -1 if the theme has none.
  const bplusApplicationSlotIndex = (themeId) => {
    const slots = buildBPlusSlots(themeId);
    if (slots) return slots.findIndex(s => s.kind === 'application');
    if (!themeSlideCounts[themeId]?.application) return -1;
    return bplusSlotCount(themeId) - 3;
  };

  // Map a UI slot index to the bplus_thumbnail entry by skipping the
  // application "No Image" slot when the theme has one. Legacy-only helper.
  const bplusThumbIdx = (themeId, index) => {
    if (!themeHasApplication(themeId)) return index;
    const noImageIdx = bplusApplicationSlotIndex(themeId);
    if (index === noImageIdx) return -1;
    return index > noImageIdx ? index - 1 : index;
  };

  // Get theme thumbnail for a specific index.
  const getThemeThumbnail = (sequenceType, index) => {
    if (!selectedTheme || !selectedTheme.id) return null;

    const themeId = selectedTheme.id;
    const entry = themeThumbnailUrls[themeId];

    if (isNewFormat(entry)) {
      if (sequenceType === 'aPlus') return entry.aplus?.[index] || null;
      const slots = buildBPlusSlots(themeId);
      return slots?.[index]?.thumb || null;
    }

    const type = sequenceType === 'bPlus' ? 'bplus' : 'aplus';
    const config = THEME_CONFIG[themeId];

    let lookupIdx = index;
    if (sequenceType === 'bPlus') {
      if (themeHasApplication(themeId)) {
        const noImageIdx = bplusApplicationSlotIndex(themeId);
        if (index === noImageIdx) {
          return entry?.app_thumbnail || null;
        }
      }
      lookupIdx = bplusThumbIdx(themeId, index);
      if (lookupIdx < 0) return null;
    }

    if (config) {
      const numbers = config[type];
      if (numbers && lookupIdx < numbers.length) {
        return loadThemeImage(themeId, type, numbers[lookupIdx]);
      }
      return null;
    }

    const total = slideCount(themeId, type);
    if (lookupIdx >= total) return null;
    return loadThemeImage(themeId, type, lookupIdx + 1);
  };

  // Check if A+ page at index needs an image. Pages flagged in APLUS_NO_IMAGE_CONFIG
  // or that consume application_images render a "No Image" placeholder instead.
  const needsImage = (sequenceType, index) => {
    if (sequenceType !== 'aPlus') return true; // B+ always needs images
    if (!selectedTheme || !selectedTheme.id) return true;
    return aplusPageNeedsImage(selectedTheme.id, index);
  };

  const getThemeTitle = (sequenceType, defaultTitle) => {
    const limit = getDynamicMaxLimit(sequenceType);
    return `${defaultTitle} (Max ${limit})`;
  };

  const renderSequence = (sequence, sequenceType, title) => {
    const fileInputRef = sequenceType === 'bPlus' ? bPlusFileInputRef : aPlusFileInputRef;
    const fileInputId = `image-upload-${sequenceType}`;
    const currentMaxLimit = getDynamicMaxLimit(sequenceType);
    const remainingSlots = currentMaxLimit - sequence.length;
    const canAddMoreImages = sequence.length < currentMaxLimit;

    // Get the number of thumbnails to show based on theme configuration
    // When a theme is selected, show all thumbnails even if there are fewer images.
    // Falls back to the backend slide schema count for themes without THEME_CONFIG.
    let itemsToRender = sequence.length;
    if (selectedTheme && selectedTheme.id) {
      const themeId = selectedTheme.id;
      const type = sequenceType === 'bPlus' ? 'bplus' : 'aplus';
      const config = THEME_CONFIG[themeId];
      if (sequenceType === 'bPlus') {
        itemsToRender = bplusSlotCount(themeId);
      } else if (config && config[type]) {
        itemsToRender = config[type].length;
      } else {
        const total = slideCount(themeId, type);
        if (total > 0) itemsToRender = total;
      }
    }

    return (
      <div className={styles.sequenceSection}>
        <div className={styles.sequenceHeader}>
          <h3 className={styles.sequenceTitle}>{title}</h3>
          <p className={styles.sequenceInstructions}>Drag to change sequence</p>
        </div>
        <div 
          className={styles.sequenceContainer}
          onDragOver={(e) => sequence.length === 0 ? handleDragOver(e, 0, sequenceType) : undefined}
          onDragLeave={sequence.length === 0 ? handleDragLeave : undefined}
          onDrop={(e) => sequence.length === 0 ? handleDrop(e, 0, sequenceType) : undefined}
        >
          {sequence.length === 0 && (
            <div 
              className={`${styles.emptyState} ${dragOverIndex.index === 0 && dragOverIndex.sequence === sequenceType ? styles.dragOver : ''}`}
            >
              <p>add images</p>
              <button
                type="button"
                className={styles.emptyStateButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={!canAddMoreImages || isLoading}
              >
                <FaPlus />
              </button>
            </div>
          )}
          {Array.from({ length: itemsToRender }, (_, index) => {
            const themeThumbnail = getThemeThumbnail(sequenceType, index);
            const pageNeedsImage = needsImage(sequenceType, index);

            // Identify special B+ slots. For new-format themes the slot kind
            // comes from the JSON ordering (features → application → zoomshot →
            // lifestyle). Legacy themes assume app at total-3, zoom at total-2.
            let isApplicationBPlus = false;
            let isZoomshotBPlus = false;
            if (sequenceType === 'bPlus' && selectedTheme && selectedTheme.id) {
              const themeId = selectedTheme.id;
              const slots = buildBPlusSlots(themeId);
              if (slots) {
                const kind = slots[index]?.kind;
                isApplicationBPlus = kind === 'application';
                isZoomshotBPlus = kind === 'zoomshot';
              } else {
                const hasZoomLifestyle = selectedTheme.has_zoom_lifestyle !== false;
                if (hasZoomLifestyle && itemsToRender >= 2) {
                  isZoomshotBPlus = index === itemsToRender - 2;
                }
                if (themeHasApplication(themeId) && itemsToRender >= 3) {
                  isApplicationBPlus = index === itemsToRender - 3;
                }
              }
            }

            // Calculate which image index to use based on how many previous pages don't need images
            let imageIndex = index;
            if (sequenceType === 'aPlus') {
              let offset = 0;
              for (let i = 0; i < index; i++) {
                if (!needsImage(sequenceType, i)) {
                  offset++;
                }
              }
              imageIndex = index - offset;
            } else if (sequenceType === 'bPlus' && selectedTheme?.id && themeHasApplication(selectedTheme.id)) {
              const noImageIdx = bplusApplicationSlotIndex(selectedTheme.id);
              if (noImageIdx >= 0 && index > noImageIdx) imageIndex = index - 1;
            }
            
            // Get the actual image to display
            // Wrap around using modulo if there are fewer images than slots
            let displayImage = null;
            let actualImageIndex = imageIndex;
            if (sequence.length > 0) {
              actualImageIndex = imageIndex % sequence.length;
              displayImage = sequence[actualImageIndex];
            }

            // Zoomshot B+ slot: shows "Add Zoom" overlay. Cycles to the next image
            // after the normal upload (feature) slots so the user can see which image
            // the zoomshot template will crop from.
            if (isZoomshotBPlus && sequenceType === 'bPlus' && sequence.length > 0) {
              const newSlots = buildBPlusSlots(selectedTheme.id);
              const normalSlotCount = newSlots
                ? newSlots.filter(s => s.kind === 'feature').length
                : itemsToRender - (themeHasApplication(selectedTheme.id) ? 3 : 2);
              const zoomImgIndex = Math.max(0, normalSlotCount) % sequence.length;
              const zoomImage = sequence[zoomImgIndex];
              const ZOOM_SLOT_DROP_INDEX = -1; // sentinel for zoom slot
              return (
                <div key={`${sequenceType}-${index}`} className={styles.imageSlotWrapper}>
                  <div
                    className={`${styles.imageSlot} ${styles.filledSlot} ${draggedItem === zoomImgIndex && draggedSequence === 'bPlus' ? styles.dragging : ''} ${dragOverIndex.index === ZOOM_SLOT_DROP_INDEX && dragOverIndex.sequence === 'bPlus' ? styles.dragOver : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, zoomImgIndex, 'bPlus')}
                    onDragOver={(e) => handleDragOver(e, ZOOM_SLOT_DROP_INDEX, 'bPlus')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, sequence.length, 'bPlus')}
                    onDragEnd={handleDragEnd}
                  >
                    <img
                      src={zoomImage.previewUrl}
                      alt="Add Zoom"
                      className={styles.sequenceImage}
                    />
                    <button
                      type="button"
                      className={styles.addZoomOverlay}
                      disabled={isLoading}
                      aria-label="Add Zoom"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddZoomModal({ show: true, image: zoomImage });
                      }}
                    >
                      <span className={styles.addZoomText}>Add Zoom</span>
                    </button>
                    <div className={styles.dragHandle}>
                      <FaGripVertical />
                    </div>
                  </div>
                  {themeThumbnail && (
                    <div className={styles.themeThumbnailContainer}>
                      <img
                        src={themeThumbnail}
                        alt={`Theme bPlus ${index + 1}`}
                        className={styles.themeThumbnail}
                      />
                    </div>
                  )}
                </div>
              );
            }
            if (isZoomshotBPlus && sequenceType === 'bPlus') {
              // Empty zoomshot slot when no images uploaded yet
              return null;
            }

            return (
              <div key={`${sequenceType}-${index}`} className={styles.imageSlotWrapper}>
                {isApplicationBPlus && sequenceType === 'bPlus' ? (
                  // Application slot — image is auto-generated, no upload needed
                  <div className={`${styles.imageSlot} ${styles.filledSlot}`}>
                    <div className={styles.noImageBox}>
                      <span className={styles.noImageText}>No Image</span>
                    </div>
                  </div>
                ) : pageNeedsImage ? (
                  <div
                    className={`${styles.imageSlot} ${styles.filledSlot} ${draggedItem === actualImageIndex && draggedSequence === sequenceType ? styles.dragging : ''} ${dragOverIndex.index === actualImageIndex && dragOverIndex.sequence === sequenceType ? styles.dragOver : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, actualImageIndex, sequenceType)}
                    onDragOver={(e) => handleDragOver(e, actualImageIndex, sequenceType)}
              onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, actualImageIndex, sequenceType)}
              onDragEnd={handleDragEnd}
            >
                    {displayImage ? (
              <img
                        src={displayImage.previewUrl}
                alt={`${sequenceType} ${index + 1}`}
                className={styles.sequenceImage}
              />
                    ) : (
                      <div className={styles.noImageBox}>
                        <span className={styles.noImageText}>No Image</span>
                      </div>
                    )}
              <div className={styles.imageOverlays}>
                <button
                  type="button"
                  className={styles.editButton}
                        onClick={() => handleEditImage(actualImageIndex, sequenceType)}
                  title="Edit"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.2356 0.437271C11.9419 0.156618 11.5514 0 11.1451 0C10.7389 0 10.3483 0.156618 10.0546 0.437271L8.96259 1.52777L4.43812 6.05224C4.24112 6.2502 4.10116 6.49762 4.03298 6.76845L3.5831 8.56795C3.54487 8.71925 3.54157 8.87726 3.57346 9.03002C3.60534 9.18278 3.67158 9.32628 3.76715 9.44965C3.86272 9.57301 3.98512 9.673 4.12506 9.74205C4.26501 9.8111 4.41883 9.84739 4.57489 9.84817C4.66017 9.84811 4.74513 9.83758 4.82784 9.81681L6.62735 9.36693C6.89848 9.29894 7.14603 9.15855 7.34355 8.96076L11.868 4.43629L12.959 3.34579C13.2482 3.05654 13.4106 2.66428 13.4106 2.25529C13.4106 1.84629 13.2482 1.45403 12.959 1.16479L12.2356 0.437271ZM6.61912 8.23376C6.55331 8.2996 6.47086 8.34633 6.38056 8.36898L4.58105 8.81937L5.03145 7.01986C5.05386 6.92944 5.10042 6.8468 5.16615 6.78079L9.32764 2.61981L10.7822 4.07382L6.61912 8.23376ZM12.2356 2.61827L11.5076 3.34527L10.0531 1.89127L10.7806 1.16427C10.8785 1.07068 11.0087 1.01845 11.1441 1.01845C11.2795 1.01845 11.4097 1.07068 11.5076 1.16427L12.2356 1.89127C12.332 1.98769 12.3862 2.11844 12.3862 2.25477C12.3862 2.3911 12.332 2.52186 12.2356 2.61827Z" fill="white"/>
                  <path d="M2.57072 13.3336H9.76875C10.4503 13.3328 11.1037 13.0617 11.5856 12.5798C12.0675 12.0978 12.3387 11.4444 12.3395 10.7629V5.93507C12.3395 5.79871 12.2853 5.66793 12.1889 5.57151C12.0925 5.47509 11.9617 5.42092 11.8253 5.42092C11.689 5.42092 11.5582 5.47509 11.4618 5.57151C11.3653 5.66793 11.3112 5.79871 11.3112 5.93507V10.7629C11.3112 11.172 11.1487 11.5643 10.8594 11.8536C10.5701 12.1428 10.1778 12.3053 9.76875 12.3053H2.57072C2.16164 12.3053 1.76932 12.1428 1.48006 11.8536C1.19079 11.5643 1.02829 11.172 1.02829 10.7629V3.56486C1.02829 3.15578 1.19079 2.76346 1.48006 2.4742C1.76932 2.18494 2.16164 2.02243 2.57072 2.02243H7.52707C7.66343 2.02243 7.79421 1.96826 7.89063 1.87184C7.98705 1.77542 8.04122 1.64464 8.04122 1.50829C8.04122 1.37193 7.98705 1.24115 7.89063 1.14473C7.79421 1.04831 7.66343 0.994141 7.52707 0.994141H2.57072C1.88918 0.994957 1.23578 1.26606 0.753849 1.74799C0.271922 2.22992 0.000816388 2.88332 0 3.56486V10.7629C0.000816388 11.4444 0.271922 12.0978 0.753849 12.5798C1.23578 13.0617 1.88918 13.3328 2.57072 13.3336Z" fill="white"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.deleteButton}
                        onClick={() => removeImageFromSequence(actualImageIndex, sequenceType)}
                  title="Delete"
                >
                  <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.4441 2.87202H10.6505L10.2946 1.49558C10.0669 0.614518 9.25126 0 8.31244 0H4.68935C3.75048 0 2.93559 0.614518 2.70717 1.49558L2.35126 2.87202H0.557193C0.249337 2.87202 0 3.11298 0 3.41049C0 3.70799 0.249337 3.94895 0.557193 3.94895H0.78146L1.3881 12.1665C1.46401 13.1943 2.359 14 3.42608 14H9.57392C10.6409 14 11.5359 13.1943 11.6119 12.1665L12.2185 3.94895H12.4428C12.7507 3.94895 13 3.70799 13 3.41049C13 3.11298 12.7507 2.87202 12.4428 2.87202H12.4441ZM3.78877 1.75673C3.89254 1.35625 4.26308 1.07692 4.69003 1.07692H8.31311C8.74006 1.07692 9.1106 1.35692 9.21437 1.75673L9.50272 2.87202H3.49967L3.78802 1.75673H3.78877ZM10.5015 12.0898C10.4673 12.557 10.0599 12.9231 9.57513 12.9231H3.42658C2.94112 12.9231 2.53438 12.557 2.50025 12.0898L1.89848 3.949H11.1026L10.5008 12.0898H10.5015ZM7.42933 9.87208V6.28256C7.42933 5.98506 7.67866 5.7441 7.98652 5.7441C8.29437 5.7441 8.54371 5.98506 8.54371 6.28256V9.87208C8.54371 10.1696 8.29437 10.4105 7.98652 10.4105C7.67866 10.4105 7.42933 10.1696 7.42933 9.87208ZM4.45811 9.87208V6.28256C4.45811 5.98506 4.70745 5.7441 5.0153 5.7441C5.32316 5.7441 5.57249 5.98506 5.57249 6.28256V9.87208C5.57249 10.1696 5.32316 10.4105 5.0153 10.4105C4.70745 10.4105 4.45811 10.1696 4.45811 9.87208Z" fill="white"/>
                  </svg>
                </button>
              </div>
              <div className={styles.dragHandle}>
                <FaGripVertical />
              </div>
            </div>
                ) : (
                  <div className={`${styles.imageSlot} ${styles.noImageSlot}`}>
                    <div className={styles.noImageBox}>
                      <span className={styles.noImageText}>No Image</span>
                    </div>
                  </div>
                )}
                {themeThumbnail && (
                  <div className={`${styles.themeThumbnailContainer} ${sequenceType === 'aPlus' ? styles.themeThumbnailContainerAPlus : ''}`}>
                    <img
                      src={themeThumbnail}
                      alt={`Theme ${sequenceType} ${index + 1}`}
                      className={styles.themeThumbnail}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {canAddMoreImages && sequence.length > 0 && sequence.length < currentMaxLimit && (
            <button
              type="button"
              className={`${styles.imageSlot} ${styles.addSlot} ${dragOverIndex.index === sequence.length && dragOverIndex.sequence === sequenceType ? styles.dragOver : ''}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              onDragOver={(e) => handleDragOver(e, sequence.length, sequenceType)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, sequence.length, sequenceType)}
            >
            <svg width="16" height="16" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4.5" y="4.5" width="24" height="24" rx="11" fill="#78B3F3"/>
            <path d="M16.5 8.17969C17.3395 8.17969 18.0195 8.86073 18.0195 9.7002V14.9805H23.2998C24.1393 14.9805 24.8203 15.6605 24.8203 16.5C24.8203 17.3395 24.1393 18.0195 23.2998 18.0195H18.0195V23.2998C18.0195 24.1393 17.3395 24.8203 16.5 24.8203C15.6605 24.8203 14.9805 24.1393 14.9805 23.2998V18.0195H9.7002C8.86073 18.0195 8.17969 17.3395 8.17969 16.5C8.17969 15.6605 8.86073 14.9805 9.7002 14.9805H14.9805V9.7002C14.9805 8.86073 15.6605 8.17969 16.5 8.17969Z" fill="white" stroke="#78B3F3" strokeWidth="0.36"/>
            </svg>
            </button>
          )}
        </div>
        
        {/* Hidden file input for this sequence */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileUpload(e, sequenceType)}
          disabled={isLoading || sequence.length >= currentMaxLimit}
          id={fileInputId}
          className={styles.fileInput}
        />
      </div>
    );
  };

  const currentEditImage = editModal.show && editModal.index !== null && editModal.sequenceType
    ? (editModal.sequenceType === 'bPlus' ? localBPlus[editModal.index] : localAPlus[editModal.index])
    : null;

  // Get dimension template thumbnail (last B+ template)
  const getDimensionThumbnail = () => {
    return dimensionsOverlay || null;
  };

  const dimensionThumbnail = getDimensionThumbnail();

  return (
    <>
      <div className={styles.container}>
        {/* Product Dimensions Section */}
        <div className={styles.dimensionsSection}>
          {/* Wrapper: left = title + inputs, right = image + thumbnail */}
          <div className={styles.dimensionsRow}>
            <div ref={productDimensionsRef} className={styles.dimensionsLeft}>
              <h3 className={styles.sequenceTitle}>Product Dimensions <span className={styles.optionalLabel}>(optional)</span></h3>
              <div className={styles.dimensionsContainer}>
            {/* Length (L) */}
            <div className={coreStyles.dimensionFieldContainer}>
              <div className={coreStyles.dimensionInputContainer}>
                <input
                  name="dimensionLength"
                  type="text"
                  value={dimensionData?.breadth || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                    if (setDimensionData) {
                      setDimensionData(prev => ({
                        ...(prev || {}),
                        length: prev?.length || '',
                        breadth: v,
                        height: prev?.height || '',
                        units: prev?.units || 'cm',
                        isComplete: !!(v && (prev?.length || ''))
                      }));
                    }
                  }}
                  disabled={isLoading}
                  placeholder="Length"
                  className={coreStyles.dimensionInput}
                />
              </div>
            </div>

            {/* Width (W) */}
            <div className={coreStyles.dimensionFieldContainer}>
              <div className={coreStyles.dimensionInputContainer}>
                <input
                  name="dimensionWidth"
                  type="text"
                  value={dimensionData?.length || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                    if (setDimensionData) {
                      setDimensionData(prev => ({
                        ...(prev || {}),
                        length: v,
                        breadth: prev?.breadth || '',
                        height: prev?.height || '',
                        units: prev?.units || 'cm',
                        isComplete: !!(v && (prev?.breadth || ''))
                      }));
                    }
                  }}
                  disabled={isLoading}
                  placeholder="Width"
                  className={coreStyles.dimensionInput}
                />
              </div>
            </div>

            {/* Height (H) */}
            <div className={coreStyles.dimensionFieldContainer}>
              <div className={coreStyles.dimensionInputContainer}>
                <input
                  name="dimensionHeight"
                  type="text"
                  value={dimensionData?.height || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                    if (setDimensionData) {
                      setDimensionData(prev => ({
                        ...(prev || {}),
                        length: prev?.length || '',
                        breadth: prev?.breadth || '',
                        height: v,
                        units: prev?.units || 'cm',
                        isComplete: !!((prev?.breadth || '') && (prev?.length || ''))
                      }));
                    }
                  }}
                  disabled={isLoading}
                  placeholder="Height"
                  className={coreStyles.dimensionInput}
                />
              </div>
            </div>

            {/* Units Dropdown */}
            <div className={coreStyles.dimensionFieldContainer} style={{ flex: '0 0 90px', minWidth: '90px' }}>
              <div className={coreStyles.dimensionInputContainer}>
                <select
                  name="dimensionUnits"
                  value={dimensionData?.units || 'cm'}
                  onChange={(e) => {
                    if (setDimensionData) {
                      setDimensionData(prev => ({
                        ...(prev || {}),
                        units: e.target.value,
                        length: prev?.length || '',
                        breadth: prev?.breadth || '',
                        height: prev?.height || '',
                        isComplete: !!((prev?.breadth || '') && (prev?.length || ''))
                      }));
                    }
                  }}
                  disabled={isLoading}
                  className={coreStyles.dimensionInput}
                  style={{ cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg%27%3E%3Cpath d=\'M1 1L6 6L11 1\' stroke=\'%23505050\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '32px' }}
                >
                  <option value="cm">cm</option>
                  <option value="inches">inches</option>
                  <option value="ft">ft</option>
                  <option value="mm">mm</option>
                  <option value="m">m</option>
                </select>
              </div>
            </div>
              </div>
            </div>
            {/* Dimension Image + Thumbnail (to the right of inputs) */}
            <div className={styles.dimensionImageContainer}>
              {dimensionData?.dimensionImage ? (
                <div className={styles.dimensionImageWithThumbnail}>
                  <div className={styles.dimensionImagePreview}>
                    <img
                      src={dimensionData.dimensionImage.previewUrl}
                      alt="Dimension"
                      className={styles.dimensionImage}
                    />
                    <div className={styles.dimensionImageOverlay}>
                      <button
                        type="button"
                        className={styles.dimensionImageDelete}
                        onClick={removeDimensionImage}
                        title="Remove image"
                      >
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="27.5" height="27.5" rx="13.75" fill="#DE524A"/>
                          <path d="M20.5606 8.85553H18.5055L18.0977 7.22349C17.8367 6.17882 16.9022 5.4502 15.8264 5.4502H11.675C10.5992 5.4502 9.66546 6.17882 9.40372 7.22349L8.99591 8.85553H6.94021C6.58746 8.85553 6.30176 9.14123 6.30176 9.49398C6.30176 9.84673 6.58746 10.1324 6.94021 10.1324H7.19718L7.89228 19.8759C7.97927 21.0945 9.00478 22.0499 10.2275 22.0499H17.2719C18.4945 22.0499 19.52 21.0946 19.6071 19.8759L20.3022 10.1324H20.5591C20.9119 10.1324 21.1976 9.84673 21.1976 9.49398C21.1976 9.14123 20.9119 8.85553 20.5591 8.85553H20.5606ZM10.6431 7.53313C10.762 7.05829 11.1865 6.72709 11.6757 6.72709H15.8272C16.3164 6.72709 16.741 7.05909 16.8599 7.53313L17.1903 8.85553H10.3118L10.6431 7.53313ZM18.3347 19.785C18.2956 20.3389 17.8287 20.773 17.2733 20.773H10.228C9.67179 20.773 9.20573 20.3389 9.16663 19.785L8.4771 10.1325H19.0235L18.3347 19.785ZM14.8145 17.1554V12.8994C14.8145 12.5466 15.1002 12.2609 15.453 12.2609C15.8057 12.2609 16.0914 12.5466 16.0914 12.8994V17.1554C16.0914 17.5082 15.8057 17.7939 15.453 17.7939C15.1002 17.7939 14.8145 17.5082 14.8145 17.1554ZM11.41 17.1554V12.8994C11.41 12.5466 11.6957 12.2609 12.0485 12.2609C12.4012 12.2609 12.6869 12.5466 12.6869 12.8994V17.1554C12.6869 17.5082 12.4012 17.7939 12.0485 17.7939C11.6957 17.7939 11.41 17.5082 11.41 17.1554Z" fill="white"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  {dimensionThumbnail && (
                    <>
                      <div className={styles.dimensionArrow}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#78B3F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={styles.dimensionThumbnailWithLabels}>
                        <div className={styles.dimensionTemplateThumbnail}>
                          <div className={styles.dimensionComposite}>
                            {dimensionData?.dimensionImage && (
                              <img
                                src={dimensionData.dimensionImage.previewUrl}
                                alt="Dimension Base"
                                className={styles.dimensionCompositeProduct}
                              />
                            )}
                            <img
                              src={dimensionThumbnail}
                              alt="Dimensions Overlay"
                              className={styles.dimensionCompositeOverlay}
                            />
                          </div>
                        </div>
                        <div className={styles.dimensionLabelRight}>Length</div>
                        <div className={styles.dimensionLabelBottom}>Width</div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  className={`${styles.dimimageSlot} ${styles.addSlot}`}
                  onClick={() => dimensionImageInputRef.current?.click()}
                  disabled={isLoading}
                >
                  <svg width="16" height="16" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4.5" y="4.5" width="24" height="24" rx="11" fill="#78B3F3"/>
                    <path d="M16.5 8.17969C17.3395 8.17969 18.0195 8.86073 18.0195 9.7002V14.9805H23.2998C24.1393 14.9805 24.8203 15.6605 24.8203 16.5C24.8203 17.3395 24.1393 18.0195 23.2998 18.0195H18.0195V23.2998C18.0195 24.1393 17.3395 24.8203 16.5 24.8203C15.6605 24.8203 14.9805 24.1393 14.9805 23.2998V18.0195H9.7002C8.86073 18.0195 8.17969 17.3395 8.17969 16.5C8.17969 15.6605 8.86073 14.9805 9.7002 14.9805H14.9805V9.7002C14.9805 8.86073 15.6605 8.17969 16.5 8.17969Z" fill="white" stroke="#78B3F3" strokeWidth="0.36"/>
                  </svg>
                </button>
              )}
              <input
                ref={dimensionImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleDimensionImageUpload}
                disabled={isLoading}
                className={styles.fileInput}
              />
            </div>
          </div>
        </div>

        {/* Product Images Header */}
        <div className={styles.productImagesHeader}>
          <h2 className={styles.productImagesTitle}>Product Images</h2>
          <div className={styles.productImagesActions}>
            <div className={styles.productImagesActionsLeft}>
              {/* REMOVE BACKGROUND BUTTON */}
              <button
                type="button"
                className={styles.removeBackgroundButton}
                onClick={() => {
                  setRemoveBgModal(true);
                  setSelectedImagesForBgRemoval(new Set());
                }}
                disabled={isLoading || localBPlus.length === 0}
              >
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.7793 0.701425C14.4289 0.366559 13.9629 0.179688 13.4782 0.179688C12.9935 0.179687 12.5274 0.366559 12.177 0.701425L10.874 2.00258L5.47555 7.40104C5.2405 7.63723 5.0735 7.93245 4.99214 8.25559L4.45537 10.4027C4.40974 10.5832 4.40581 10.7718 4.44386 10.954C4.48191 11.1363 4.56094 11.3075 4.67497 11.4547C4.789 11.6019 4.93504 11.7212 5.10202 11.8036C5.269 11.886 5.45254 11.9293 5.63873 11.9302C5.74049 11.9301 5.84186 11.9176 5.94056 11.8928L8.08767 11.356C8.41117 11.2749 8.70655 11.1074 8.94222 10.8714L14.3407 5.47293L15.6424 4.17178C15.9875 3.82665 16.1813 3.35863 16.1813 2.87063C16.1813 2.38262 15.9875 1.9146 15.6424 1.56947L14.7793 0.701425ZM8.07786 10.004C7.99933 10.0825 7.90095 10.1383 7.79321 10.1653L5.64609 10.7027L6.18349 8.55557C6.21023 8.44768 6.26578 8.34908 6.34421 8.27031L11.3096 3.30557L13.0451 5.04044L8.07786 10.004ZM14.7793 3.30373L13.9106 4.17116L12.1752 2.43629L13.0432 1.56886C13.16 1.45719 13.3153 1.39487 13.4769 1.39487C13.6385 1.39487 13.7939 1.45719 13.9106 1.56886L14.7793 2.43629C14.8943 2.55134 14.9589 2.70734 14.9589 2.87001C14.9589 3.03268 14.8943 3.18869 14.7793 3.30373Z" fill="#2766AA" stroke="#2766AA" strokeWidth="0.357951" />
                  <path d="M3.24699 16.0883H11.8355C12.6487 16.0873 13.4283 15.7639 14.0033 15.1888C14.5783 14.6138 14.9018 13.8342 14.9028 13.021V7.2606C14.9028 7.0979 14.8381 6.94186 14.7231 6.82681C14.608 6.71177 14.452 6.64714 14.2893 6.64714C14.1266 6.64714 13.9706 6.71177 13.8555 6.82681C13.7405 6.94186 13.6758 7.0979 13.6758 7.2606V13.021C13.6758 13.5091 13.4819 13.9772 13.1368 14.3223C12.7917 14.6675 12.3236 14.8614 11.8355 14.8614H3.24699C2.75889 14.8614 2.29078 14.6675 1.94565 14.3223C1.60051 13.9772 1.40661 13.5091 1.40661 13.021V4.43254C1.40661 3.94444 1.60051 3.47633 1.94565 3.13119C2.29078 2.78605 2.75889 2.59216 3.24699 2.59216H9.16076C9.32346 2.59216 9.4795 2.52752 9.59454 2.41248C9.70959 2.29743 9.77422 2.1414 9.77422 1.9787C9.77422 1.816 9.70959 1.65996 9.59454 1.54491C9.4795 1.42987 9.32346 1.36523 9.16076 1.36523H3.24699C2.43379 1.36621 1.65418 1.68968 1.07916 2.2647C0.504136 2.83972 0.180662 3.61934 0.179688 4.43254V13.021C0.180662 13.8342 0.504136 14.6138 1.07916 15.1888C1.65418 15.7639 2.43379 16.0873 3.24699 16.0883Z" fill="#2766AA" stroke="#2766AA" strokeWidth="0.357951" />
                </svg>
                <span>Remove Background</span>
              </button>
              {/* AMAZON IMAGES BUTTON (placeholder) */}
              {/* <button
                type="button"
                className={styles.amazonImagesButton}
                disabled={isLoading}
              >
                <span>Add Amazon Images</span>
              </button> */}
            </div>
            <div className={styles.productImagesActionsRight}>
              {/* DELETE BUTTON */}
              <button
                type="button"
                className={styles.removeDeleteButton}
                onClick={() => {
                  setDeleteImagesModal(true);
                  setSelectedImagesForDeletion(new Set());
                }}
                disabled={isLoading || allImages.length === 0}
              >
                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.8329 3.0648H10.9834L10.6163 1.59597C10.3815 0.655767 9.54037 0 8.5722 0H4.83589C3.86769 0 3.02733 0.655767 2.79177 1.59597L2.42474 3.0648H0.574605C0.257129 3.0648 0 3.32193 0 3.63941C0 3.95688 0.257129 4.21401 0.574605 4.21401H0.805881L1.43147 12.9831C1.50976 14.0799 2.43272 14.9397 3.53315 14.9397H9.87311C10.9735 14.9397 11.8964 14.08 11.9748 12.9831L12.6004 4.21401H12.8316C13.1491 4.21401 13.4063 3.95688 13.4063 3.63941C13.4063 3.32193 13.1491 3.0648 12.8316 3.0648H12.8329ZM3.90716 1.87465C4.01418 1.44729 4.3963 1.14921 4.83659 1.14921H8.5729C9.01318 1.14921 9.39531 1.448 9.50232 1.87465L9.79968 3.0648H3.60903L3.90716 1.87465ZM10.8296 12.9014C10.7944 13.3998 10.3743 13.7905 9.87436 13.7905H3.53366C3.03303 13.7905 2.61358 13.3998 2.57838 12.9014L1.95781 4.21407H11.4495L10.8296 12.9014ZM7.66149 10.5347V6.70427C7.66149 6.38679 7.91862 6.12966 8.2361 6.12966C8.55357 6.12966 8.8107 6.38679 8.8107 6.70427V10.5347C8.8107 10.8522 8.55357 11.1093 8.2361 11.1093C7.91862 11.1093 7.66149 10.8522 7.66149 10.5347ZM4.59743 10.5347V6.70427C4.59743 6.38679 4.85455 6.12966 5.17203 6.12966C5.48951 6.12966 5.74663 6.38679 5.74663 6.70427V10.5347C5.74663 10.8522 5.48951 11.1093 5.17203 11.1093C4.85455 11.1093 4.59743 10.8522 4.59743 10.5347Z" fill="#DE524A"/>
                </svg>
                <span>Delete Images</span>
              </button>
              {/* RESTORE DELETED BUTTON */}
              {deletedImages.length > 0 && (
                <button
                  type="button"
                  className={styles.restoreDeletedButton}
                  onClick={() => {
                    setRestoreImagesModal(true);
                    setSelectedImagesForRestore(new Set());
                  }}
                  disabled={isLoading}
                >
                  <span>Restore Deleted</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* B+ Product Image Sequence */}
        {renderSequence(localBPlus, 'bPlus', getThemeTitle('bPlus', 'B+ Product Image Sequence'))}

        {/* A+ Product Image Sequence */}
        <div className={styles.aPlusSequenceContainer}>
          <div className={styles.aPlusSequenceHeader}>
            <h3 className={`${styles.sequenceTitle} ${styles.aPlusSequenceTitle}`}>{getThemeTitle('aPlus', 'A+ Product Image Sequence')}</h3>
            <label className={styles.copyCheckboxLabel}>
              <input
                type="checkbox"
                checked={copyFromBPlus}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setCopyFromBPlus(isChecked);
                  if (isChecked) {
                    // When checked, copy B+ images to A+
                    if (localBPlus && localBPlus.length > 0) {
                      const copiedImages = localBPlus.map(img => ({
                        ...img,
                        file: img.file,
                        previewUrl: img.previewUrl,
                        imagePath: img.imagePath
                      }));
                      setLocalAPlus(copiedImages);
                      if (onAPlusSequenceChange) {
                        onAPlusSequenceChange(copiedImages);
                      }
                    }
                  }
                  // When unchecked, do nothing - keep the A+ images as they are
                }}
                disabled={isLoading}
                className={styles.copyCheckboxInput}
              />
              <div className={`${styles.copyCheckboxBox} ${copyFromBPlus ? styles.copyCheckboxBoxChecked : ''}`}>
                {copyFromBPlus && (
                  <svg width="9" height="6.54" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
      </div>
              <div className={styles.copyCheckboxText}>
                Copy images from B+ Images
              </div>
            </label>
          </div>
          {renderSequence(localAPlus, 'aPlus', '')}
        </div>
      </div>

      {/* Modals */}
      <EditImageModal
        isOpen={editModal.show}
        onClose={closeEditModal}
        image={currentEditImage}
        onRemoveBackground={handleRemoveBackground}
        processingImage={processingImage}
        isLoading={isLoading}
      />

      <RemoveBackgroundModal
        isOpen={removeBgModal}
        onClose={() => setRemoveBgModal(false)}
        uniqueImages={uniqueImages}
        selectedImages={selectedImagesForBgRemoval}
        onToggleSelection={handleToggleImageSelection}
        onSelectAll={handleSelectAll}
        onRemoveBackground={handleRemoveBackgroundMultiple}
        processingBgRemoval={processingBgRemoval}
      />

      <DeleteImagesModal
        isOpen={deleteImagesModal}
        onClose={() => setDeleteImagesModal(false)}
        allImages={allImages}
        selectedImages={selectedImagesForDeletion}
        onToggleSelection={handleToggleImageSelectionForDeletion}
        onSelectAll={handleSelectAllForDeletion}
        onDeleteSelected={handleDeleteSelectedImages}
        isVariants={false}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={cancelDeleteImages}
        onConfirm={confirmDeleteImages}
      />

      <RestoreImagesModal
        isOpen={restoreImagesModal}
        onClose={() => setRestoreImagesModal(false)}
        deletedImages={deletedImages}
        selectedImages={selectedImagesForRestore}
        onToggleSelection={handleToggleImageSelectionForRestore}
        onSelectAll={handleSelectAllForRestore}
        onRestoreSelected={handleRestoreSelectedImages}
      />

      <AddZoomModal
        isOpen={addZoomModal.show}
        onClose={() => setAddZoomModal({ show: false, image: null })}
        image={addZoomModal.image}
        zoomThumbnail={zoomshotBgUrls[selectedTheme?.id] || null}
        onAddZoomAreas={() => {
          const zoomThumbnailImg = zoomshotBgUrls[selectedTheme?.id] || null;
          setAddZoomModal({ show: false, image: null });
          setAddZoomAreasModal({
            show: true,
            image: addZoomModal.image,
            zoomThumbnail: zoomThumbnailImg,
            zoomshots: selectedTheme?.zoomshot_info?.zoomshot_positions || [],
            imageBbox: selectedTheme?.zoomshot_info?.image_bbox || [0, 0, 100, 100]
          });
        }}
      />

      <AddZoomAreasModal
        isOpen={addZoomAreasModal.show}
        onClose={(areas) => {
          // Always persist (even an empty list) so the user's "clear" action sticks.
          if (Array.isArray(areas)) onZoomAreasChange?.(areas);
          setAddZoomAreasModal({ show: false, image: null });
        }}
        sourceImage={addZoomAreasModal.image}
        zoomThumbnail={addZoomAreasModal.zoomThumbnail}
        zoomshots={addZoomAreasModal.zoomshots || []}
        imageBbox={addZoomAreasModal.imageBbox || [0, 0, 100, 100]}
        initialZoomAreas={zoomAreas}
      />
    </>
  );
};

export default ImageUploadStep;
